import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Form } from 'antd'
import { useRecoilValue } from 'recoil'
import healthClaimFormAtom from '@/recoil/healthClaimForm/atom'
import { ClaimChannelEnum, HealthClaimModeEnum } from '@/constant/E_CLAIM'
import { useHealthClaimForm } from '@/contexts/healthClaimForm'
import { useHospitals } from '@/services/healthClaim/useHospital'
import { useDiseases } from '@/services/healthClaim/useDiseases'
import { TreatmentTypeSelectHook } from '../TreatmentTypeSelect/TreatmentTypeSelect.hook'
import { HospitalOptionType } from './types'
import dayjs from 'dayjs'
import { useLazyQuery } from '@apollo/client'
import { userGql } from '@/gql'

export const TreatmentDetailPanelHook = () => {
  const healthClaim = useRecoilValue(healthClaimFormAtom)
  const [opdLeftCount, setOpdLeftCount] = useState<string>(healthClaim.data?.opdLeft || '')
  const isCreateOrEditMode =
    healthClaim.mode === HealthClaimModeEnum.CREATE || healthClaim.mode === HealthClaimModeEnum.EDIT
  const healthClaimForm = useHealthClaimForm()
  const {
    forms: { treatmentDetailForm },
    selectedPolicy,
    selectedPolicyNumber,
  } = healthClaimForm
  const isAppClaimChannel = healthClaim.data?.claimChannel === ClaimChannelEnum.SUPERAPP
  const isRequired = { required: true, message: 'กรุณากรอกข้อมูลนี้' }
  const resolvedSelectedPolicyNumber = selectedPolicyNumber || ''

  // State for disease search and pagination
  const [diseaseSearchTerm, setDiseaseSearchTerm] = useState('')
  const [diseasePage, setDiseasePage] = useState(1)
  const [allDiseases, setAllDiseases] = useState<any[]>([])
  const [hasMoreDiseases, setHasMoreDiseases] = useState(true)
  // State to track if we need to load initial diseases for preset diseaseId
  const [shouldLoadInitialDiseases, setShouldLoadInitialDiseases] = useState(false)
  // State to control loading more to prevent multiple requests
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  // Get treatment types data to determine type name from ID
  const { data: treatmentTypes } = TreatmentTypeSelectHook({
    policyNumber: resolvedSelectedPolicyNumber,
  })

  // Get hospitals data from API
  const { data: hospitalsResponse, isLoading: isLoadingHospitals } = useHospitals()

  // Get policy details by policy number
  const [backofficeGetUserPolicyByPolicyNumber] = useLazyQuery(
    userGql.query.backofficeGetUserPolicyByPolicyNumber,
    {
      fetchPolicy: 'no-cache',
    },
  )

  // Watch disease fields reactively to avoid race conditions with form initialization
  const watchedDiseaseId: string | undefined = Form.useWatch('diseaseId', treatmentDetailForm)
  const watchedDiseaseCode: string | undefined = Form.useWatch('diseaseCode', treatmentDetailForm)

  // Separate API calls for initial loading and manual search
  // API call for initial loading: allow triggering when we have either diseaseCode or diseaseId (or data from healthClaim)
  const initialSearch = (
    watchedDiseaseCode ||
    healthClaim.data?.disease?.code ||
    healthClaim.data?.disease?.name ||
    watchedDiseaseId ||
    healthClaim.data?.disease?.id ||
    ''
  ).trim()
  const shouldCallInitialAPI =
    shouldLoadInitialDiseases && initialSearch.length > 0 && !diseaseSearchTerm
  const { data: initialDiseasesResponse, isLoading: isLoadingInitialDiseases } = useDiseases({
    page: 1,
    limit: 50,
    search: initialSearch,
    enabled: shouldCallInitialAPI,
    key: healthClaim.data?.claimFormId,
    debounceMs: 0, // do not debounce initial prefill fetch to avoid empty windows
  })

  // API call for manual search (only when user is searching)
  const { data: searchDiseasesResponse, isLoading: isLoadingSearchDiseases } = useDiseases({
    page: diseasePage,
    limit: 50,
    search: diseaseSearchTerm,
    enabled: !!diseaseSearchTerm,
    key: healthClaim.data?.claimFormId,
  })

  const isLoadingDiseases = isLoadingInitialDiseases || isLoadingSearchDiseases

  // Check if component has a preset diseaseId or diseaseCode on mount
  useEffect(() => {
    const hasPreset =
      Boolean(watchedDiseaseCode) ||
      Boolean(healthClaim.data?.disease?.code) ||
      Boolean(healthClaim.data?.disease?.name) ||
      Boolean(watchedDiseaseId) ||
      Boolean(healthClaim.data?.disease?.id)
    if (hasPreset && allDiseases.length === 0 && !diseaseSearchTerm) {
      setShouldLoadInitialDiseases(true)
    }
  }, [
    watchedDiseaseCode,
    watchedDiseaseId,
    healthClaim.data?.disease?.code,
    healthClaim.data?.disease?.name,
    healthClaim.data?.disease?.id,
    allDiseases.length,
    diseaseSearchTerm,
  ])

  // Reset disease-related state when switching between claims to avoid stale options
  useEffect(() => {
    setAllDiseases([])
    setDiseasePage(1)
    setHasMoreDiseases(true)
    setIsLoadingMore(false)
    setSelectedDisease(null)
    setDiseaseSearchTerm('')
    setShouldLoadInitialDiseases(false)
  }, [healthClaim.data?.claimFormId])

  // Watch treatment type to show/hide fields
  const [selectedTreatmentType, setSelectedTreatmentType] = useState<string>('')
  const [selectedTreatmentTypeName, setSelectedTreatmentTypeName] = useState<string>('')
  const isOtherHospital: boolean = Form.useWatch('isOtherHospital', treatmentDetailForm) || false
  // State to track selected values for custom display
  const [selectedHospital, setSelectedHospital] = useState<any>(null)
  const [selectedDisease, setSelectedDisease] = useState<any>(null)

  // Define options with useMemo to prevent re-renders
  const hospitalOptions = useMemo(() => {
    if (!hospitalsResponse) return []

    return hospitalsResponse.map((hospital) => ({
      value: hospital.id,
      label: hospital.name,
      code: hospital.code,
      fullName: hospital.name,
    }))
  }, [hospitalsResponse])

  // Effect to handle initial diseases response (for preset diseaseCode)
  useEffect(() => {
    if (initialDiseasesResponse && shouldLoadInitialDiseases) {
      setAllDiseases(initialDiseasesResponse.data)
      setShouldLoadInitialDiseases(false) // Mark as loaded to prevent re-loading
    }
  }, [initialDiseasesResponse, shouldLoadInitialDiseases])

  // Effect to handle search diseases response (for manual search)
  useEffect(() => {
    if (searchDiseasesResponse && diseaseSearchTerm) {
      if (diseasePage === 1) {
        // First page - replace all diseases
        setAllDiseases(searchDiseasesResponse.data)
      } else {
        // Subsequent pages - append to existing diseases
        setAllDiseases((prev) => [...prev, ...searchDiseasesResponse.data])
      }

      // Check if there are more pages
      setHasMoreDiseases(diseasePage < searchDiseasesResponse.meta.totalPages)
      setIsLoadingMore(false)
    }
  }, [searchDiseasesResponse, diseasePage, diseaseSearchTerm])

  // Effect to reset pagination when search term changes
  useEffect(() => {
    if (diseaseSearchTerm) {
      // Reset data for new search
      setDiseasePage(1)
      setAllDiseases([])
      setHasMoreDiseases(true)
      setIsLoadingMore(false)
    }
  }, [diseaseSearchTerm])

  // Define disease options from API response
  const icd10Options = useMemo(() => {
    return allDiseases.map((disease) => ({
      value: disease.id,
      label: `${disease.nameTh} - ${disease.icdCode}`,
      code: disease.icdCode,
      thaiName: disease.nameTh,
      englishName: disease.nameEn,
    }))
  }, [allDiseases])

  const treatmentTypeId = treatmentDetailForm.getFieldValue('treatmentTypeId')

  useEffect(() => {
    setSelectedTreatmentType(treatmentTypeId || '')
    // Find the treatment type name from the ID
    if (treatmentTypeId && treatmentTypes) {
      const treatmentType = treatmentTypes.find((t) => t.id === treatmentTypeId)
      setSelectedTreatmentTypeName(treatmentType?.name || '')
    }
  }, [treatmentTypeId, treatmentTypes])

  // Watch for initial form values
  useEffect(() => {
    const currentHospitalId = treatmentDetailForm.getFieldValue('hospitalId')
    const currentDiseaseId = watchedDiseaseId

    // Set initial hospital selection - only if hospital data is loaded and not already set
    if (currentHospitalId && hospitalOptions.length > 0 && !selectedHospital) {
      const hospital = hospitalOptions.find(
        (h: HospitalOptionType) => h.value === currentHospitalId,
      )
      if (hospital) {
        setSelectedHospital(hospital)
      }
    }

    // Set initial disease selection from available options - only if disease data is loaded and not already set
    if (currentDiseaseId && icd10Options.length > 0 && !selectedDisease) {
      const disease = icd10Options.find((d) => d.value === currentDiseaseId)
      if (disease) {
        setSelectedDisease(disease)
      }
    }
  }, [treatmentDetailForm, hospitalOptions, icd10Options, selectedHospital, selectedDisease])

  // Separate effect to handle hospital initialization when hospital data loads
  useEffect(() => {
    const currentHospitalId = treatmentDetailForm.getFieldValue('hospitalId')

    if (currentHospitalId && hospitalOptions.length > 0 && !selectedHospital) {
      const hospital = hospitalOptions.find(
        (h: HospitalOptionType) => h.value === currentHospitalId,
      )
      if (hospital) {
        setSelectedHospital(hospital)
      }
    }
  }, [hospitalOptions, selectedHospital, treatmentDetailForm])

  // Separate effect to handle disease initialization when disease data loads
  useEffect(() => {
    const currentDiseaseId = watchedDiseaseId
    const currentDiseaseCode = watchedDiseaseCode

    if (currentDiseaseId && icd10Options.length > 0 && !selectedDisease) {
      // First try to find by diseaseId
      let disease = icd10Options.find((d) => d.value === currentDiseaseId)

      // If not found by diseaseId and we have diseaseCode, try to find by diseaseCode
      if (!disease && currentDiseaseCode) {
        disease = icd10Options.find((d) => d.code === currentDiseaseCode)
      }

      if (disease) {
        setSelectedDisease(disease)
        // Update the form with the correct diseaseId if we found by diseaseCode
        if (disease.value !== currentDiseaseId) {
          treatmentDetailForm.setFieldValue('diseaseId', disease.value)
        }
      }
    }
  }, [icd10Options, selectedDisease, watchedDiseaseId, watchedDiseaseCode, treatmentDetailForm])

  // Helper functions to determine which fields to show based on treatment type
  const showAccidentFields = () => {
    const name = selectedTreatmentTypeName?.toLowerCase() || ''
    const hasAccident = name.includes('อุบัติเหตุ')
    const hasIpd = name.includes('ipd') || name.includes('ผู้ป่วยใน')

    return hasAccident && !hasIpd
  }

  const showOpdFields = () => {
    const name = selectedTreatmentTypeName?.toLowerCase() || ''
    const hasOpd = name.includes('opd') || name.includes('ผู้ป่วยนอก')
    const hasAccident = name.includes('อุบัติเหตุ')

    return hasOpd && !hasAccident
  }

  const showIpdFields = () => {
    return (
      selectedTreatmentTypeName &&
      (selectedTreatmentTypeName.toLowerCase().includes('ipd') ||
        selectedTreatmentTypeName.toLowerCase().includes('ผู้ป่วยใน'))
    )
  }

  const showHibFields = () => {
    return (
      selectedTreatmentTypeName && selectedTreatmentTypeName.toLowerCase().includes('ชดเชยรายได้')
    )
  }

  const isTreatmentSelected = selectedTreatmentType && selectedTreatmentType !== ''

  // Mock disease data - in real implementation, this would come from the health claim data
  const diseaseInfo = {
    userSpecified: healthClaim.data?.selfReportedDisease || '',
    icd10: '',
  }

  // Handler functions
  const handleTreatmentTypeChange = (value: string | undefined) => {
    setSelectedTreatmentType(value || '')
    // Find the treatment type name from the ID
    if (value && treatmentTypes) {
      const treatmentType = treatmentTypes.find((t) => t.id === value)
      setSelectedTreatmentTypeName(treatmentType?.name || '')
    } else {
      setSelectedTreatmentTypeName('')
    }
  }

  const handleHospitalChange = (value: any) => {
    const hospital = hospitalOptions.find((h: HospitalOptionType) => h.value === value)
    setSelectedHospital(hospital)
    treatmentDetailForm.setFieldValue('hospitalId', value)
    // Revalidate to clear any stale validation errors
    treatmentDetailForm.validateFields(['hospitalId']).catch(() => {})
  }

  const handleDiseaseChange = (value: any) => {
    const disease = icd10Options.find((d) => d.value === value)
    setSelectedDisease(disease)
    treatmentDetailForm.setFieldValue('diseaseId', value)
    setDiseaseSearchTerm('')
  }

  const handleDiseaseSearch = (value: string) => {
    // Reset shouldLoadInitialDiseases when user starts manual search
    if (shouldLoadInitialDiseases) {
      setShouldLoadInitialDiseases(false)
    }
    setDiseaseSearchTerm(value)
  }

  const handleDiseaseBlur = () => {
    const currentDiseaseId = treatmentDetailForm.getFieldValue('diseaseId')
    setDiseaseSearchTerm('')

    const isValidSelection = icd10Options.some((disease) => disease.value === currentDiseaseId)

    if (!currentDiseaseId || !isValidSelection) {
      treatmentDetailForm.setFieldValue('diseaseId', undefined)
      setSelectedDisease(null)
    }
  }

  const handleDiseaseDropdownVisibleChange = (open: boolean) => {
    if (!open) {
      // Reset search when dropdown closes
      setDiseaseSearchTerm('')
      const currentDiseaseId = treatmentDetailForm.getFieldValue('diseaseId')
      const isValidSelection = icd10Options.some((disease) => disease.value === currentDiseaseId)

      if (!currentDiseaseId || !isValidSelection) {
        treatmentDetailForm.setFieldValue('diseaseId', undefined)
        setSelectedDisease(null)
      }
    }
  }

  const handleDiseasePopupScroll = (e: any) => {
    const { target } = e
    const element = target as HTMLElement

    // Check if we've scrolled to near the bottom (with buffer)
    const scrollBottom = element.scrollTop + element.offsetHeight
    const scrollHeight = element.scrollHeight
    const threshold = 200 // Larger buffer - load earlier

    if (
      scrollBottom >= scrollHeight - threshold &&
      hasMoreDiseases &&
      !isLoadingDiseases &&
      !isLoadingMore
    ) {
      // Set loading more flag to prevent multiple requests
      setIsLoadingMore(true)
      // Load more diseases when near bottom
      setDiseasePage((prev) => prev + 1)
    }
  }

  // Keep previous selections for toggling back-and-forth
  const prevHospitalIdRef = useRef<string | undefined>(undefined)
  const prevOtherHospitalNameRef = useRef<string | undefined>(undefined)
  const prevPolicyNumberRef = useRef<string | undefined>(undefined)

  // Clear treatment type when switching policy after initial load
  useEffect(() => {
    // Skip on first initialization to preserve prefilled data
    if (prevPolicyNumberRef.current === undefined) {
      prevPolicyNumberRef.current = resolvedSelectedPolicyNumber
      return
    }
    if (prevPolicyNumberRef.current !== resolvedSelectedPolicyNumber) {
      // Reset local state and form field to avoid stale UI
      setSelectedTreatmentType('')
      setSelectedTreatmentTypeName('')
      treatmentDetailForm.setFieldValue('treatmentTypeId', '')
      prevPolicyNumberRef.current = resolvedSelectedPolicyNumber
    }
  }, [resolvedSelectedPolicyNumber, treatmentDetailForm])

  const handleOtherHospitalChange = (e: any) => {
    const checked = e.target.checked
    treatmentDetailForm.setFieldValue('isOtherHospital', checked)
    if (checked) {
      // Save current contracted hospital to restore later
      const currentHospitalId = treatmentDetailForm.getFieldValue('hospitalId')
      if (currentHospitalId) prevHospitalIdRef.current = currentHospitalId
      // Switch to other hospital mode: clear contracted hospital
      treatmentDetailForm.setFieldValue('hospitalId', undefined)
      // Prefill previous other hospital name if current is empty
      const currentOtherName = treatmentDetailForm.getFieldValue('otherHospitalName')
      if (!currentOtherName && prevOtherHospitalNameRef.current) {
        treatmentDetailForm.setFieldValue('otherHospitalName', prevOtherHospitalNameRef.current)
      }
    } else {
      // Save current other hospital name to restore later
      const currentOtherName = treatmentDetailForm.getFieldValue('otherHospitalName')
      if (currentOtherName) prevOtherHospitalNameRef.current = currentOtherName
      // Switch back to contracted hospital mode: clear other name and restore hospitalId
      treatmentDetailForm.setFieldValue('otherHospitalName', '')
      if (prevHospitalIdRef.current) {
        treatmentDetailForm.setFieldValue('hospitalId', prevHospitalIdRef.current)
      }
    }
    // Revalidate affected fields to update UI immediately
    treatmentDetailForm.validateFields(['hospitalId', 'otherHospitalName']).catch(() => {})
  }

  const handleTreatmentDateInDisabledDate = (current: Dayjs) => {
    if (!current) return false

    if (!selectedPolicy) return true

    const isAccident = showAccidentFields()
    const isHib = showHibFields()
    const isIpd = showIpdFields()
    const accidentDate = treatmentDetailForm.getFieldValue('accidentDate')
    const effectiveDate = selectedPolicy.effectiveDate
    const expiryDate = selectedPolicy.expiryDate
    const treatmentDateOut = treatmentDetailForm.getFieldValue('treatmentDateOut')

    if (!effectiveDate) return true

    const todayEnd = dayjs().endOf('day')
    const yesterdayEnd = dayjs().subtract(1, 'day').endOf('day')

    // Max date logic:
    // - Default: up to today
    // - HIB: capped at yesterday
    let maxDate = todayEnd
    if (expiryDate) {
      const policyEnd = dayjs(expiryDate).endOf('day')
      if (policyEnd.isBefore(maxDate)) maxDate = policyEnd
    }
    // If treatmentDateOut is set, cap maxDate to treatmentDateOut (for IPD and HIB)
    if (treatmentDateOut && (isIpd || isHib)) {
      const outEnd = dayjs(treatmentDateOut).endOf('day')
      if (outEnd.isBefore(maxDate)) maxDate = outEnd
    }

    // HIB cannot select today or future dates
    if (isHib && maxDate.isAfter(yesterdayEnd)) {
      maxDate = yesterdayEnd
    }

    // Min date logic:
    // - PA(Accident): from accident date if provided, otherwise from effective date
    // - HIB: from effective date until yesterday
    // - Others: from effective date

    const minDate =
      isAccident && accidentDate
        ? dayjs(accidentDate).startOf('day')
        : dayjs(effectiveDate).startOf('day')

    return current.isBefore(minDate) || current.isAfter(maxDate)
  }

  const handleTreatmentDateOutDisabledDate = (current: Dayjs) => {
    if (!current) return false

    const treatmentDateIn = treatmentDetailForm.getFieldValue('treatmentDateIn')
    const todayEnd = dayjs().endOf('day')

    // No future dates
    if (current.isAfter(todayEnd)) return true

    if (treatmentDateIn) {
      // HIB requires +1 day; IPD allows same-day discharge
      const isHib = showHibFields()
      const minOut = isHib
        ? dayjs(treatmentDateIn).add(1, 'day').startOf('day')
        : dayjs(treatmentDateIn).startOf('day')
      return current.isBefore(minOut)
    }

    // If no treatment date in, allow any past date
    return false
  }

  const handleAccidentDateDisabledDate = (current: Dayjs) => {
    if (!current) return false

    if (!selectedPolicy) return true

    const effectiveDate = selectedPolicy.effectiveDate
    const expiryDate = selectedPolicy.expiryDate
    const treatmentDateIn = treatmentDetailForm.getFieldValue('treatmentDateIn')
    if (!effectiveDate) return true

    const todayEnd = dayjs().endOf('day')
    const minDate = dayjs(effectiveDate).startOf('day')
    let maxDate = todayEnd
    if (expiryDate) {
      const policyEnd = dayjs(expiryDate).endOf('day')
      if (policyEnd.isBefore(maxDate)) maxDate = policyEnd
    }
    if (treatmentDateIn) {
      const inEnd = dayjs(treatmentDateIn).endOf('day')
      if (inEnd.isBefore(maxDate)) maxDate = inEnd
    }

    return current.isBefore(minDate) || current.isAfter(maxDate)
  }

  const getOpdLeftCountByPolicyNumber = useCallback(
    async (policyNumber: string) => {
      const policy = await backofficeGetUserPolicyByPolicyNumber({
        variables: { policyNumber },
      })

      if (policy.data?.backofficeGetUserPolicyByPolicyNumber) {
        const opdLeft = policy.data.backofficeGetUserPolicyByPolicyNumber.opdLeft || ''
        setOpdLeftCount(opdLeft)
      }
    },
    [backofficeGetUserPolicyByPolicyNumber],
  )

  // Fetch OPD left count when selected policy number changes
  useEffect(() => {
    if (resolvedSelectedPolicyNumber) {
      getOpdLeftCountByPolicyNumber(resolvedSelectedPolicyNumber)
    }
  }, [resolvedSelectedPolicyNumber, getOpdLeftCountByPolicyNumber])

  return {
    // Form and state
    treatmentDetailForm,
    isCreateOrEditMode,
    isRequired,
    selectedPolicyNumber: resolvedSelectedPolicyNumber,
    opdLeftCount,
    healthClaim,
    isAppClaimChannel,

    // Treatment type state
    selectedTreatmentType,
    selectedTreatmentTypeName,
    treatmentTypes,

    // Hospital state
    hospitalOptions,
    selectedHospital,
    isLoadingHospitals,
    isOtherHospital,

    // Disease state
    icd10Options,
    selectedDisease,
    isLoadingDiseases,
    diseaseSearchTerm,
    hasMoreDiseases,
    isLoadingMore,
    diseaseInfo,

    // Helper functions
    showAccidentFields,
    showOpdFields,
    showIpdFields,
    showHibFields,
    isTreatmentSelected,

    // Handler functions
    handleTreatmentTypeChange,
    handleHospitalChange,
    handleDiseaseChange,
    handleDiseaseSearch,
    handleDiseaseBlur,
    handleDiseaseDropdownVisibleChange,
    handleDiseasePopupScroll,
    handleOtherHospitalChange,
    handleTreatmentDateInDisabledDate,
    handleTreatmentDateOutDisabledDate,
    handleAccidentDateDisabledDate,
  }
}
